// Packed-splat helpers: pack RGB into the half-float layout Spark uses, and
// extract per-Gaussian positions / env-splat centroid bounds from a packed
// array. All purely numeric; no THREE-state dependency beyond the
// packedSplats accessor.

import * as THREE from 'three';
import { fromHalf } from '@sparkjsdev/spark';

// setPackedSplatRgb is defined in spark's source but NOT in its ESM exports,
// so inline it here (mirrors src/main.js's treatment of setPackedSplatCenter).
// Packed layout: word 0 = [B<<16 | G<<8 | R] in bits 0..23, alpha in bits
// 24..31. Float [0,1] → uint8 with default rgbMin=0, rgbMax=1.
export function setPackedSplatRgb(pa, index, r, g, b) {
  const uR = r <= 0 ? 0 : r >= 1 ? 255 : Math.round(r * 255);
  const uG = g <= 0 ? 0 : g >= 1 ? 255 : Math.round(g * 255);
  const uB = b <= 0 ? 0 : b >= 1 ? 255 : Math.round(b * 255);
  const i4 = index * 4;
  pa[i4] = (uR | (uG << 8) | (uB << 16) | (pa[i4] & 0xFF000000)) >>> 0;
}

// Merge P rgba8unorm words (the WGSL shade's pack4x8unorm output) into the
// packed splat array: RGB comes from src[i], the alpha byte (splat opacity)
// keeps the packed array's existing value. u32-loop counterpart of a per-splat
// setPackedSplatRgb pass — same quantisation (pack4x8unorm rounds to nearest,
// matching Math.round(v*255) after the [0,1] clamp).
export function mergePackedRgb(pa, src, P) {
  for (let i = 0; i < P; i++) {
    const i4 = i * 4;
    pa[i4] = ((src[i] & 0x00FFFFFF) | (pa[i4] & 0xFF000000)) >>> 0;
  }
}

// Decode positions from the packed half-float layout into a flat Float32Array
// (P*3) and return the centroid in the same authored frame.
export function extractPositionsFromPackedSplats(packedSplats, P) {
  const pa = packedSplats.packedArray;
  const positions = new Float32Array(P * 3);
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < P; i++) {
    const base = i * 4;
    const x = fromHalf(pa[base + 1] & 0xFFFF);
    const y = fromHalf((pa[base + 1] >> 16) & 0xFFFF);
    const z = fromHalf(pa[base + 2] & 0xFFFF);
    positions[i * 3]     = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    cx += x; cy += y; cz += z;
  }
  const centroid = new THREE.Vector3(cx / P, cy / P, cz / P);
  return { positions, centroid };
}

// Compute centroid + AABB + a generous "far" hint from an env SplatMesh. The
// loft env was captured from a viewpoint roughly inside the room, so its
// centroid is the natural probe origin — that's where all 6 cube faces see
// the room walls. Returns null if envMesh has no packedSplats.
export function computeEnvCentroidBounds(envMesh) {
  if (!envMesh?.packedSplats) return null;
  const pa = envMesh.packedSplats.packedArray;
  const E = envMesh.numSplats ?? Math.floor(pa.length / 4);
  let ecx = 0, ecy = 0, ecz = 0;
  let xMin = Infinity, yMin = Infinity, zMin = Infinity;
  let xMax = -Infinity, yMax = -Infinity, zMax = -Infinity;
  for (let i = 0; i < E; i++) {
    const base = i * 4;
    const x = fromHalf(pa[base + 1] & 0xFFFF);
    const y = fromHalf((pa[base + 1] >> 16) & 0xFFFF);
    const z = fromHalf(pa[base + 2] & 0xFFFF);
    ecx += x; ecy += y; ecz += z;
    if (x < xMin) xMin = x; if (x > xMax) xMax = x;
    if (y < yMin) yMin = y; if (y > yMax) yMax = y;
    if (z < zMin) zMin = z; if (z > zMax) zMax = z;
  }
  const envCentroid = new THREE.Vector3(ecx / E, ecy / E, ecz / E);
  const envMin = new THREE.Vector3(xMin, yMin, zMin);
  const envMax = new THREE.Vector3(xMax, yMax, zMax);
  // Generous far: 4× the longest bounding-box dimension, so splats at the
  // far edge of the room never clip even if the probe sits near one wall.
  const ext = Math.max(xMax - xMin, yMax - yMin, zMax - zMin);
  const envFarHint = Math.max(1000, ext * 4);
  return { envCentroid, envMin, envMax, envFarHint };
}
